import { useEffect, useState, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAnalysisStore } from '@/store/analysisStore'
import { trackFunnelEvent } from '@/services/analytics'

// Decorative sparkle component
const Sparkle = ({ className, size = 24, color = '#FF8C33', delay = 0 }: { className?: string; size?: number; color?: string; delay?: number }) => (
  <motion.svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    animate={{
      scale: [1, 1.3, 1],
      opacity: [0.7, 1, 0.7],
      rotate: [0, 15, 0]
    }}
    transition={{
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut",
      delay: delay
    }}
  >
    <path d="M12 0L13.5 9L22 12L13.5 15L12 24L10.5 15L2 12L10.5 9L12 0Z" />
  </motion.svg>
)

// Confetti piece component
const ConfettiPiece = ({ delay, x, color }: { delay: number; x: number; color: string }) => (
  <motion.div
    className="absolute w-3 h-3 rounded-sm"
    style={{ backgroundColor: color, left: `${x}%` }}
    initial={{ y: -20, opacity: 1, rotate: 0 }}
    animate={{
      y: ['0vh', '100vh'],
      opacity: [1, 1, 0],
      rotate: [0, 360, 720]
    }}
    transition={{
      duration: 3,
      delay: delay,
      ease: "easeIn",
      repeat: Infinity,
      repeatDelay: 2
    }}
  />
)

// Checkmark animation component
const AnimatedCheckmark = () => (
  <motion.div
    className="w-24 h-24 rounded-full bg-[#7ED957] flex items-center justify-center mx-auto mb-6"
    initial={{ scale: 0 }}
    animate={{ scale: 1 }}
    transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
  >
    <motion.svg
      width="48"
      height="48"
      viewBox="0 0 24 24"
      fill="none"
      stroke="white"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 0.5, delay: 0.5 }}
    >
      <motion.path
        d="M5 13l4 4L19 7"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 0.5, delay: 0.5 }}
      />
    </motion.svg>
  </motion.div>
)

const steps = [
  { number: 1, title: 'Few More Questions', description: 'Take a new selfie (optional) & answer a few questions', emoji: '📋' },
  { number: 2, title: 'AI Analysis', description: '14 HD metrics analyzed', emoji: '🔬' },
  { number: 3, title: 'Your Routine', description: 'Personalized AM & PM plan', emoji: '✨' },
]

const confettiColors = ['#FF8C33', '#F596FA', '#7182F8', '#46C0C6', '#7ED957']

export function PaymentSuccessPage() {
  const navigate = useNavigate()
  const { hasHDResults } = useAnalysisStore()
  const [_showContent, setShowContent] = useState(false)

  // Track payment completed on page load (once)
  const hasTrackedPayment = useRef(false)
  useEffect(() => {
    if (!hasTrackedPayment.current) {
      hasTrackedPayment.current = true
      trackFunnelEvent.paymentCompleted(2900)
    }
  }, [])

  // Auto-redirect to report if already completed full analysis
  const hasRedirected = useRef(false)
  useEffect(() => {
    if (hasHDResults() && !hasRedirected.current) {
      hasRedirected.current = true
      navigate('/report', { state: { showWelcomeToast: true } })
    }
  }, [hasHDResults, navigate])

  useEffect(() => {
    // Show content after initial animation
    const timer = setTimeout(() => setShowContent(true), 300)
    return () => clearTimeout(timer)
  }, [])

  const handleContinue = () => {
    navigate('/full-survey')
  }

  return (
    <div className="min-h-screen bg-[#FFFEF1] flex flex-col overflow-hidden relative">
      {/* Confetti */}
      {confettiColors.map((color, i) => (
        <ConfettiPiece key={`c1-${i}`} delay={i * 0.2} x={10 + i * 18} color={color} />
      ))}
      {confettiColors.map((color, i) => (
        <ConfettiPiece key={`c2-${i}`} delay={0.5 + i * 0.15} x={15 + i * 17} color={color} />
      ))}

      {/* Header */}
      <header className="bg-[#FFFEF1] py-4">
        <div className="max-w-2xl mx-auto px-4 flex items-center justify-center">
          <Link to="/" style={{ fontFamily: "'Oleo Script', cursive" }} className="text-[18px]">
            <span className="text-[#688192]">K-Beauty</span>
            <span className="text-[#46C0C6]"> AI</span>
          </Link>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 max-w-md mx-auto px-6 py-8 w-full flex flex-col items-center justify-center">
        {/* Sparkles around the checkmark */}
        <div className="relative">
          <Sparkle className="absolute -top-4 -left-8" size={20} color="#FF8C33" delay={0} />
          <Sparkle className="absolute -top-2 -right-10" size={16} color="#F596FA" delay={0.3} />
          <Sparkle className="absolute top-12 -left-12" size={14} color="#7182F8" delay={0.6} />
          <Sparkle className="absolute top-10 -right-8" size={18} color="#46C0C6" delay={0.9} />

          {/* Animated checkmark */}
          <AnimatedCheckmark />
        </div>

        {/* Success message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="text-center mb-8"
        >
          <h1
            className="text-[28px] font-bold text-[#075D61] mb-2"
            style={{ fontFamily: "'Anybody', sans-serif" }}
          >
            Payment Successful!
          </h1>
          <p
            className="text-[16px] text-[#656565]"
            style={{ fontFamily: "'SF Pro', -apple-system, sans-serif" }}
          >
            Your K-Beauty journey begins now
          </p>
        </motion.div>

        {/* What's next section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="w-full mb-8"
        >
          <h2
            className="text-[18px] text-[#E8A87C] mb-4 text-center"
            style={{ fontFamily: "'Pattaya', cursive" }}
          >
            What's Next
          </h2>

          <div className="space-y-3">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1 + index * 0.15 }}
                className="flex items-center gap-4 bg-white rounded-[16px] p-4 shadow-sm"
              >
                <div className="w-10 h-10 rounded-full bg-[#46C0C6] flex items-center justify-center text-white font-bold text-[14px]">
                  {step.number}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-[#075D61] text-[15px]">{step.title}</p>
                  <p className="text-[13px] text-[#656565]">{step.description}</p>
                </div>
                <span className="text-[24px]">{step.emoji}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4 }}
          className="w-full"
        >
          <motion.button
            onClick={handleContinue}
            className="w-full h-[54px] bg-[#7ED957] rounded-[30px] text-white text-[18px] font-semibold shadow-lg"
            style={{ fontFamily: "'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif" }}
            animate={{
              boxShadow: [
                "0 0 0 0 rgba(126, 217, 87, 0.4)",
                "0 0 0 12px rgba(126, 217, 87, 0)",
                "0 0 0 0 rgba(126, 217, 87, 0)"
              ]
            }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Let's Go! →
          </motion.button>
        </motion.div>

        {/* Reassurance text */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          className="text-[12px] text-[#999] text-center mt-4"
        >
          Just a few more questions to personalize your routine
        </motion.p>
      </main>
    </div>
  )
}
